import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Trophy, Users, Flame, Plus, ChevronRight, ChevronDown,
  Target, Crown, Medal, UserPlus, X, Loader2, Search, Send
} from "lucide-react";
import {
  getChallenges, getChallengeDetail, createChallenge, joinChallenge,
  leaveChallenge, deleteChallenge, inviteToChallenge, inviteAllToChallenge,
  searchChallengeMembers, getHabits
} from "@/lib/api";
import { toast } from "sonner";

const DAY_OPTIONS = [
  { value: "sunday", label: "Sun" }, { value: "monday", label: "Mon" },
  { value: "tuesday", label: "Tue" }, { value: "wednesday", label: "Wed" },
  { value: "thursday", label: "Thu" }, { value: "friday", label: "Fri" },
  { value: "saturday", label: "Sat" },
];

function ProgressBar({ value, max = 100, className = "" }) {
  const pct = max > 0 ? Math.min(Math.round((value / max) * 100), 100) : 0;
  return (
    <div className={`w-full h-2 rounded-full bg-[#F0EBE0] overflow-hidden ${className}`}>
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: "var(--liturgical-primary)" }} />
    </div>
  );
}

function LeaderboardEntry({ entry, index }) {
  const rankIcons = { 1: Crown, 2: Medal, 3: Medal };
  const RankIcon = rankIcons[index + 1];
  const rankColors = { 0: "text-amber-500", 1: "text-gray-400", 2: "text-amber-700" };
  return (
    <div className={`flex items-center gap-3 py-2.5 px-3 rounded-lg ${entry.is_current_user ? "bg-[var(--liturgical-primary)]/5 border border-[var(--liturgical-primary)]/20" : ""}`} data-testid={`leaderboard-entry-${entry.user_id}`}>
      <div className="w-6 text-center flex-shrink-0">
        {RankIcon ? <RankIcon className={`w-4 h-4 mx-auto ${rankColors[index] || "text-[#6B665E]"}`} /> : <span className="font-['Nunito'] text-sm font-bold text-[#6B665E]">{index + 1}</span>}
      </div>
      <div className="w-8 h-8 rounded-full bg-[#F0EBE0] flex items-center justify-center flex-shrink-0 overflow-hidden">
        {entry.profile_photo ? <img src={entry.profile_photo} alt="" className="w-full h-full object-cover" /> : <span className="font-['Nunito'] text-xs font-bold text-[#6B665E]">{(entry.name || "?")[0]}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-['Nunito'] text-sm font-medium truncate text-[#2D2A26]">{entry.name}{entry.is_current_user && " (you)"}</p>
        <p className="font-['Nunito'] text-xs text-[#9B968E]">{entry.completions} / {entry.total_possible} check-offs</p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="font-['Nunito'] text-sm font-bold" style={{ color: "var(--liturgical-primary)" }}>{entry.score}%</p>
      </div>
    </div>
  );
}

function ChallengeCard({ challenge, onView, onJoin }) {
  const daysLeft = Math.max(0, Math.ceil((new Date(challenge.end_date) - new Date()) / 86400000));
  return (
    <Card className="border-[#E5E0D5] bg-white hover:shadow-md transition-shadow" data-testid={`challenge-card-${challenge.challenge_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-['Crimson_Text'] text-base font-semibold text-[#2D2A26] truncate">{challenge.title}</h3>
            {challenge.description && <p className="font-['Nunito'] text-xs text-[#6B665E] mt-0.5 line-clamp-2">{challenge.description}</p>}
          </div>
          <Trophy className="w-4 h-4 flex-shrink-0 mt-0.5 ml-2" style={{ color: "var(--liturgical-primary)" }} />
        </div>
        <div className="flex flex-wrap gap-1 mt-2 mb-3">
          {challenge.habit_names?.slice(0, 3).map((name, i) => (
            <Badge key={i} variant="outline" className="text-[10px] border-[#E5E0D5] text-[#6B665E]">{name}</Badge>
          ))}
          {(challenge.habit_names?.length || 0) > 3 && <Badge variant="outline" className="text-[10px] border-[#E5E0D5] text-[#6B665E]">+{challenge.habit_names.length - 3}</Badge>}
        </div>
        <div className="flex items-center justify-between text-xs font-['Nunito'] text-[#9B968E] mb-3">
          <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {challenge.participant_count} participants</span>
          {challenge.is_active ? <span className="flex items-center gap-1"><Flame className="w-3 h-3 text-orange-500" /> {daysLeft}d left</span>
           : daysLeft > 0 ? <span>Starts {new Date(challenge.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
           : <span className="text-[#9B968E]">Ended</span>}
        </div>
        <div className="flex gap-2">
          {challenge.is_joined ? (
            <Button size="sm" onClick={() => onView(challenge.challenge_id)} className="flex-1 h-8 text-xs bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid={`view-challenge-${challenge.challenge_id}`}>
              View Progress <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          ) : (
            <Button size="sm" onClick={() => onJoin(challenge.challenge_id)} className="flex-1 h-8 text-xs bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid={`join-challenge-${challenge.challenge_id}`}>
              <UserPlus className="w-3 h-3 mr-1" /> Join Challenge
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Habit item in the create form with frequency settings
function ChallengeHabitItem({ habit, onUpdate, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border border-[#E5E0D5] rounded-lg p-2.5 bg-white" data-testid={`challenge-habit-item-${habit.name}`}>
      <div className="flex items-center justify-between">
        <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1.5 flex-1 min-w-0 text-left">
          <ChevronDown className={`w-3 h-3 text-[#6B665E] transition-transform ${expanded ? "rotate-180" : ""}`} />
          <span className="font-['Nunito'] text-sm font-medium text-[#2D2A26] truncate">{habit.name}</span>
          <span className="font-['Nunito'] text-[10px] text-[#9B968E] flex-shrink-0">
            {habit.period === "week" && habit.target_days?.length > 0
              ? habit.target_days.map(d => d.slice(0, 3)).join(", ")
              : habit.target_count > 1 ? `${habit.target_count}x/${habit.period}` : `1x/${habit.period}`}
          </span>
        </button>
        <button onClick={() => onRemove(habit.name)} className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 flex-shrink-0">
          <X className="w-3 h-3" />
        </button>
      </div>
      {expanded && (
        <div className="mt-2 pt-2 border-t border-[#E5E0D5]/50 space-y-2">
          <div className="flex items-center gap-2">
            <label className="font-['Nunito'] text-xs text-[#6B665E] w-16 flex-shrink-0">Period</label>
            <select
              value={habit.period}
              onChange={(e) => onUpdate({ ...habit, period: e.target.value, target_days: e.target.value === "day" ? [] : habit.target_days })}
              className="flex-1 text-xs font-['Nunito'] border border-[#E5E0D5] rounded px-2 py-1 bg-white"
              data-testid={`habit-period-${habit.name}`}
            >
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="font-['Nunito'] text-xs text-[#6B665E] w-16 flex-shrink-0">Times</label>
            <select
              value={habit.target_count}
              onChange={(e) => onUpdate({ ...habit, target_count: parseInt(e.target.value) })}
              className="flex-1 text-xs font-['Nunito'] border border-[#E5E0D5] rounded px-2 py-1 bg-white"
              data-testid={`habit-target-${habit.name}`}
            >
              {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}x per {habit.period}</option>)}
            </select>
          </div>
          {habit.period === "week" && (
            <div>
              <label className="font-['Nunito'] text-xs text-[#6B665E] mb-1 block">On days</label>
              <div className="flex gap-1">
                {DAY_OPTIONS.map(d => (
                  <button
                    key={d.value}
                    onClick={() => {
                      const days = habit.target_days?.includes(d.value)
                        ? habit.target_days.filter(x => x !== d.value)
                        : [...(habit.target_days || []), d.value];
                      onUpdate({ ...habit, target_days: days });
                    }}
                    className={`px-1.5 py-0.5 rounded text-[10px] font-['Nunito'] font-medium border transition-colors ${
                      habit.target_days?.includes(d.value)
                        ? "text-white border-transparent"
                        : "bg-white border-[#E5E0D5] text-[#6B665E]"
                    }`}
                    style={habit.target_days?.includes(d.value) ? { backgroundColor: "var(--liturgical-primary)" } : {}}
                    data-testid={`habit-day-${habit.name}-${d.value}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ChallengesTab({ parishId, groupId }) {
  const [challenges, setChallenges] = useState([]);
  const [browseChallenges, setBrowseChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [userHabits, setUserHabits] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteSearch, setInviteSearch] = useState("");
  const [inviteResults, setInviteResults] = useState([]);
  const [inviteSearching, setInviteSearching] = useState(false);
  const [selectedInvites, setSelectedInvites] = useState([]);
  const [inviteAllLoading, setInviteAllLoading] = useState(false);

  const defaultChallenge = {
    title: "", description: "", habits: [], habit_names: [],
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    leaderboard_type: "aggregate",
    parish_id: parishId || null,
    group_id: groupId || null,
    invited_user_ids: [],
  };
  const [newChallenge, setNewChallenge] = useState(defaultChallenge);
  const [customHabitName, setCustomHabitName] = useState("");

  const scope = parishId ? "parish" : groupId ? "group" : "my";

  const loadChallenges = useCallback(async () => {
    try {
      const params = {};
      if (parishId) params.parish_id = parishId;
      if (groupId) params.group_id = groupId;
      const [scopeRes, browseRes] = await Promise.all([
        getChallenges(scope, params),
        getChallenges("browse"),
      ]);
      setChallenges(scopeRes.data.challenges || []);
      setBrowseChallenges(browseRes.data.challenges || []);
    } catch (err) {
      console.error("Failed to load challenges:", err);
    }
    setLoading(false);
  }, [scope, parishId, groupId]);

  const loadUserHabits = useCallback(async () => {
    try {
      const res = await getHabits();
      setUserHabits(res.data.habits || []);
    } catch {}
  }, []);

  useEffect(() => {
    loadChallenges();
    loadUserHabits();
  }, [loadChallenges, loadUserHabits]);

  const handleViewDetail = async (challengeId) => {
    setDetailLoading(true);
    setShowDetail(true);
    try {
      const res = await getChallengeDetail(challengeId);
      setSelectedChallenge(res.data);
    } catch {
      toast.error("Failed to load challenge details");
      setShowDetail(false);
    }
    setDetailLoading(false);
  };

  const handleJoin = async (challengeId) => {
    try {
      const res = await joinChallenge(challengeId);
      toast.success(`Joined challenge! ${res.data.habits_added > 0 ? `${res.data.habits_added} habits added to your tracker.` : ""}`);
      await loadChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to join challenge");
    }
  };

  const handleLeave = async (challengeId) => {
    try {
      await leaveChallenge(challengeId);
      toast.success("Left the challenge");
      setShowDetail(false);
      await loadChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to leave challenge");
    }
  };

  const handleDelete = async (challengeId) => {
    try {
      await deleteChallenge(challengeId);
      toast.success("Challenge deleted");
      setShowDetail(false);
      await loadChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete challenge");
    }
  };

  const handleCreate = async () => {
    if (!newChallenge.title.trim() || (newChallenge.habits.length === 0 && newChallenge.habit_names.length === 0)) {
      toast.error("Please add a title and at least one habit");
      return;
    }
    try {
      const res = await createChallenge({
        ...newChallenge,
        invited_user_ids: selectedInvites.map(u => u.user_id),
      });
      const createdId = res.data.challenge_id;
      toast.success("Challenge created!");

      // If invite-all was toggled, send invites to all members
      if (newChallenge.invite_all && createdId) {
        try {
          const invRes = await inviteAllToChallenge(createdId);
          if (invRes.data.sent > 0) {
            toast.success(`Invited ${invRes.data.sent} ${contextLabel.toLowerCase()} members!`);
          }
        } catch {}
      }

      setShowCreate(false);
      setNewChallenge({ ...defaultChallenge });
      setSelectedInvites([]);
      await loadChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create challenge");
    }
  };

  const addHabitToChallenge = (name) => {
    if (!name.trim()) return;
    const existing = newChallenge.habits.find(h => h.name.toLowerCase() === name.toLowerCase());
    if (existing) return;
    setNewChallenge(prev => ({
      ...prev,
      habits: [...prev.habits, { name: name.trim(), period: "day", target_count: 1, target_days: [] }],
    }));
  };

  const updateHabitInChallenge = (updated) => {
    setNewChallenge(prev => ({
      ...prev,
      habits: prev.habits.map(h => h.name === updated.name ? updated : h),
    }));
  };

  const removeHabitFromChallenge = (name) => {
    setNewChallenge(prev => ({
      ...prev,
      habits: prev.habits.filter(h => h.name !== name),
    }));
  };

  const addCustomHabit = () => {
    if (customHabitName.trim()) {
      addHabitToChallenge(customHabitName.trim());
      setCustomHabitName("");
    }
  };

  // Invite search - scoped to group/parish if applicable
  const handleInviteSearch = async () => {
    if (!inviteSearch.trim()) return;
    setInviteSearching(true);
    try {
      const params = { query: inviteSearch, limit: 20 };
      if (parishId) params.parish_id = parishId;
      if (groupId) params.group_id = groupId;
      // If viewing detail of a scoped challenge, use its scope
      if (selectedChallenge?.parish_id) params.parish_id = selectedChallenge.parish_id;
      if (selectedChallenge?.group_id) params.group_id = selectedChallenge.group_id;
      const res = await searchChallengeMembers(params);
      setInviteResults(res.data.users || []);
    } catch { toast.error("Search failed"); }
    setInviteSearching(false);
  };

  const handleInviteFromDetail = async () => {
    if (!selectedChallenge || selectedInvites.length === 0) return;
    try {
      const res = await inviteToChallenge(selectedChallenge.challenge_id, selectedInvites.map(u => u.user_id));
      toast.success(`${res.data.sent} invitation${res.data.sent !== 1 ? 's' : ''} sent!`);
      setShowInvite(false);
      setSelectedInvites([]);
      setInviteSearch("");
      setInviteResults([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invites");
    }
  };

  const handleInviteAll = async (challengeId) => {
    setInviteAllLoading(true);
    try {
      const res = await inviteAllToChallenge(challengeId || selectedChallenge?.challenge_id);
      const sent = res.data.sent || 0;
      if (sent > 0) {
        toast.success(`Invited ${sent} ${contextLabel.toLowerCase()} member${sent !== 1 ? 's' : ''}!`);
      } else {
        toast.info("All members are already participants or invited.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to invite all");
    }
    setInviteAllLoading(false);
  };

  const toggleInviteUser = (user) => {
    setSelectedInvites(prev =>
      prev.find(u => u.user_id === user.user_id)
        ? prev.filter(u => u.user_id !== user.user_id)
        : [...prev, user]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--liturgical-primary)" }} />
      </div>
    );
  }

  const contextLabel = parishId ? "Parish" : groupId ? "Group" : "Community";

  return (
    <div className="space-y-4" data-testid="challenges-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26]">{contextLabel} Challenges</h2>
          <p className="font-['Nunito'] text-xs text-[#6B665E]">Grow together with your {contextLabel.toLowerCase()} community</p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] text-xs h-8" data-testid="create-challenge-btn">
          <Plus className="w-3 h-3 mr-1" /> New Challenge
        </Button>
      </div>

      {/* My / Scoped Challenges */}
      {challenges.length > 0 && (
        <div>
          <h3 className="font-['Nunito'] text-sm font-semibold text-[#2D2A26] mb-2">{parishId || groupId ? `${contextLabel} Challenges` : "My Challenges"}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {challenges.map(ch => <ChallengeCard key={ch.challenge_id} challenge={ch} onView={handleViewDetail} onJoin={handleJoin} />)}
          </div>
        </div>
      )}

      {browseChallenges.length > 0 && (
        <div>
          <h3 className="font-['Nunito'] text-sm font-semibold text-[#2D2A26] mb-2">Discover Challenges</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {browseChallenges.map(ch => <ChallengeCard key={ch.challenge_id} challenge={ch} onView={handleViewDetail} onJoin={handleJoin} />)}
          </div>
        </div>
      )}

      {challenges.length === 0 && browseChallenges.length === 0 && (
        <Card className="border-[#E5E0D5] bg-white">
          <CardContent className="p-8 text-center">
            <Trophy className="w-10 h-10 mx-auto mb-3 opacity-40" style={{ color: "var(--liturgical-primary)" }} />
            <p className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-1">No Challenges Yet</p>
            <p className="font-['Nunito'] text-sm text-[#6B665E] mb-4">Create the first challenge for your {contextLabel.toLowerCase()}!</p>
            <Button onClick={() => setShowCreate(true)} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid="create-first-challenge-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Create a Challenge
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Challenge Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="bg-white border-[#E5E0D5] max-w-lg max-h-[85vh]">
          {detailLoading ? (
            <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--liturgical-primary)" }} /></div>
          ) : selectedChallenge && (
            <>
              <DialogHeader>
                <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
                  <Trophy className="w-5 h-5" style={{ color: "var(--liturgical-primary)" }} />
                  {selectedChallenge.title}
                </DialogTitle>
                {selectedChallenge.description && (
                  <DialogDescription className="font-['Nunito'] text-sm text-[#6B665E]">{selectedChallenge.description}</DialogDescription>
                )}
              </DialogHeader>
              <ScrollArea className="max-h-[60vh] pr-2">
                <div className="space-y-4 py-2">
                  <div className="bg-[#F9F7F2] rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-['Nunito'] text-xs font-medium text-[#6B665E]">Group Progress</span>
                      <span className="font-['Nunito'] text-sm font-bold" style={{ color: "var(--liturgical-primary)" }}>{selectedChallenge.aggregate_score}%</span>
                    </div>
                    <ProgressBar value={selectedChallenge.aggregate_score} />
                    <div className="flex items-center justify-between mt-2 text-[10px] font-['Nunito'] text-[#9B968E]">
                      <span>{selectedChallenge.elapsed_days} / {selectedChallenge.total_days} days</span>
                      <span>{selectedChallenge.participant_count} participants</span>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-['Nunito'] text-xs font-semibold text-[#6B665E] mb-1.5">Tracked Habits</h4>
                    <div className="flex flex-wrap gap-1">
                      {selectedChallenge.habit_names?.map((name, i) => {
                        const detail = selectedChallenge.habits_detail?.find(h => h.name === name);
                        const freq = detail ? (detail.period === "week" && detail.target_days?.length > 0
                          ? detail.target_days.map(d => d.slice(0, 3)).join(", ")
                          : detail.target_count > 1 ? `${detail.target_count}x/${detail.period}` : `1x/${detail.period}`) : "";
                        return (
                          <Badge key={i} variant="outline" className="text-[10px] border-[#E5E0D5] text-[#6B665E]">
                            {name}{freq && ` (${freq})`}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-['Nunito'] text-xs font-semibold text-[#6B665E] mb-1.5 flex items-center gap-1">
                      <Target className="w-3 h-3" /> Leaderboard
                    </h4>
                    <div className="space-y-1">
                      {selectedChallenge.leaderboard?.map((entry, i) => <LeaderboardEntry key={entry.user_id} entry={entry} index={i} />)}
                    </div>
                  </div>

                  <div className="text-center font-['Nunito'] text-xs text-[#9B968E]">
                    {new Date(selectedChallenge.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {new Date(selectedChallenge.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>

                  <div className="flex flex-col gap-2 pt-2">
                    {selectedChallenge.is_joined && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => { setShowInvite(true); setSelectedInvites([]); setInviteSearch(""); setInviteResults([]); }} className="flex-1 h-8 text-xs bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid="invite-to-challenge-btn">
                          <UserPlus className="w-3 h-3 mr-1" /> Invite Members
                        </Button>
                        {selectedChallenge.is_creator && (parishId || groupId || selectedChallenge.parish_id || selectedChallenge.group_id) && (
                          <Button size="sm" variant="outline" onClick={() => handleInviteAll()} disabled={inviteAllLoading} className="flex-1 h-8 text-xs border-[var(--liturgical-primary)]/30 text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/5 font-['Nunito']" data-testid="invite-all-members-btn">
                            {inviteAllLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Users className="w-3 h-3 mr-1" />}
                            Invite All {contextLabel} Members
                          </Button>
                        )}
                      </div>
                    )}
                    {selectedChallenge.is_creator ? (
                      <Button variant="outline" size="sm" onClick={() => handleDelete(selectedChallenge.challenge_id)} className="border-red-200 text-red-500 hover:bg-red-50 font-['Nunito'] text-xs h-8" data-testid="delete-challenge-btn">
                        Delete Challenge
                      </Button>
                    ) : selectedChallenge.is_joined ? (
                      <Button variant="outline" size="sm" onClick={() => handleLeave(selectedChallenge.challenge_id)} className="border-[#E5E0D5] text-[#6B665E] font-['Nunito'] text-xs h-8" data-testid="leave-challenge-btn">
                        Leave Challenge
                      </Button>
                    ) : null}
                  </div>
                </div>
              </ScrollArea>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Invite Members Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="bg-white border-[#E5E0D5] max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
              <UserPlus className="w-5 h-5" style={{ color: "var(--liturgical-primary)" }} /> Invite Members
            </DialogTitle>
            <DialogDescription className="font-['Nunito'] text-sm text-[#6B665E]">
              Search and invite members to this challenge.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex gap-2">
              <Input value={inviteSearch} onChange={(e) => setInviteSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleInviteSearch()} placeholder={`Search ${parishId || groupId ? contextLabel.toLowerCase() : ''} members...`} className="flex-1 border-[#E5E0D5] font-['Nunito']" data-testid="challenge-invite-search" />
              <Button onClick={handleInviteSearch} disabled={inviteSearching} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white">
                {inviteSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
            {selectedInvites.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {selectedInvites.map(u => (
                  <Badge key={u.user_id} className="text-[10px] flex items-center gap-1 pr-1" style={{ backgroundColor: "var(--liturgical-primary)", color: "white" }}>
                    {u.name}
                    <button onClick={() => toggleInviteUser(u)} className="hover:opacity-70"><X className="w-2.5 h-2.5" /></button>
                  </Badge>
                ))}
              </div>
            )}
            <ScrollArea className="max-h-48">
              <div className="space-y-1">
                {inviteResults.map(u => {
                  const alreadyIn = selectedChallenge?.participants?.includes(u.user_id);
                  const selected = selectedInvites.find(s => s.user_id === u.user_id);
                  return (
                    <button key={u.user_id} onClick={() => !alreadyIn && toggleInviteUser(u)} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#F9F7F2] text-left ${alreadyIn ? "opacity-50" : ""}`} disabled={alreadyIn} data-testid={`invite-user-${u.user_id}`}>
                      <div className="w-8 h-8 rounded-full bg-[#F0EBE0] flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {u.profile_photo ? <img src={u.profile_photo} alt="" className="w-full h-full object-cover" /> : <span className="font-['Nunito'] text-xs font-bold text-[#6B665E]">{(u.name || "?")[0]}</span>}
                      </div>
                      <span className="font-['Nunito'] text-sm font-medium text-[#2D2A26] truncate flex-1">{u.name}</span>
                      {alreadyIn ? <span className="font-['Nunito'] text-xs text-[#9B968E]">Already in</span>
                       : selected ? <Badge className="text-[10px]" style={{ backgroundColor: "var(--liturgical-primary)", color: "white" }}>Selected</Badge>
                       : null}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvite(false)} className="border-[#E5E0D5] font-['Nunito']">Cancel</Button>
            <Button onClick={handleInviteFromDetail} disabled={selectedInvites.length === 0} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid="send-invites-btn">
              <Send className="w-3 h-3 mr-1.5" /> Send {selectedInvites.length} Invite{selectedInvites.length !== 1 ? "s" : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Challenge Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-white border-[#E5E0D5] max-w-md max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
              <Trophy className="w-5 h-5" style={{ color: "var(--liturgical-primary)" }} /> New Challenge
            </DialogTitle>
            <DialogDescription className="font-['Nunito'] text-sm text-[#6B665E]">
              Create a challenge for your {contextLabel.toLowerCase()}.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-2">
            <div className="space-y-4 py-2">
              <div>
                <label className="font-['Nunito'] text-sm text-[#2D2A26] font-medium">Title</label>
                <Input value={newChallenge.title} onChange={(e) => setNewChallenge(prev => ({ ...prev, title: e.target.value }))} placeholder="e.g., Lenten Rosary Challenge" className="mt-1 border-[#E5E0D5] font-['Nunito']" data-testid="challenge-title-input" />
              </div>
              <div>
                <label className="font-['Nunito'] text-sm text-[#2D2A26] font-medium">Description (optional)</label>
                <Input value={newChallenge.description} onChange={(e) => setNewChallenge(prev => ({ ...prev, description: e.target.value }))} placeholder="What's the goal?" className="mt-1 border-[#E5E0D5] font-['Nunito']" data-testid="challenge-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-['Nunito'] text-sm text-[#2D2A26] font-medium">Start Date</label>
                  <Input type="date" value={newChallenge.start_date} onChange={(e) => setNewChallenge(prev => ({ ...prev, start_date: e.target.value }))} className="mt-1 border-[#E5E0D5] font-['Nunito']" data-testid="challenge-start-date" />
                </div>
                <div>
                  <label className="font-['Nunito'] text-sm text-[#2D2A26] font-medium">End Date</label>
                  <Input type="date" value={newChallenge.end_date} onChange={(e) => setNewChallenge(prev => ({ ...prev, end_date: e.target.value }))} className="mt-1 border-[#E5E0D5] font-['Nunito']" data-testid="challenge-end-date" />
                </div>
              </div>

              {/* Habit Selection with Frequency */}
              <div>
                <label className="font-['Nunito'] text-sm text-[#2D2A26] font-medium">Habits to Track</label>
                <p className="font-['Nunito'] text-xs text-[#9B968E] mb-2">Add habits and customize their frequency. Click a habit to expand settings.</p>

                {/* Quick-add from existing habits */}
                {userHabits.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {userHabits.filter(h => !newChallenge.habits.find(ch => ch.name.toLowerCase() === h.name.toLowerCase())).map(h => (
                      <button
                        key={h.habit_id}
                        onClick={() => setNewChallenge(prev => ({ ...prev, habits: [...prev.habits, { name: h.name, period: h.period || "day", target_count: h.target_count || 1, target_days: h.target_days || [] }] }))}
                        className="px-2.5 py-1 rounded-full text-xs font-['Nunito'] font-medium border bg-white border-[#E5E0D5] text-[#6B665E] hover:border-[var(--liturgical-primary)]/40 transition-colors"
                        data-testid={`quick-add-habit-${h.habit_id}`}
                      >
                        + {h.name}
                      </button>
                    ))}
                  </div>
                )}

                {/* Added habits with frequency */}
                {newChallenge.habits.length > 0 && (
                  <div className="space-y-1.5 mb-2">
                    {newChallenge.habits.map(h => (
                      <ChallengeHabitItem key={h.name} habit={h} onUpdate={updateHabitInChallenge} onRemove={removeHabitFromChallenge} />
                    ))}
                  </div>
                )}

                {/* Custom habit input */}
                <div className="flex gap-2">
                  <Input
                    value={customHabitName}
                    onChange={(e) => setCustomHabitName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomHabit())}
                    placeholder="Add custom habit name..."
                    className="flex-1 border-[#E5E0D5] font-['Nunito'] text-sm h-8"
                    data-testid="challenge-custom-habit-input"
                  />
                  <Button size="sm" variant="outline" onClick={addCustomHabit} className="border-[#E5E0D5] h-8 px-2" data-testid="add-custom-habit-btn">
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              {/* Invite Members in create flow */}
              <div>
                <label className="font-['Nunito'] text-sm text-[#2D2A26] font-medium">Invite Members (optional)</label>
                <p className="font-['Nunito'] text-xs text-[#9B968E] mb-2">They'll receive a notification to join</p>
                {(parishId || groupId) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setNewChallenge(prev => ({ ...prev, invite_all: !prev.invite_all }))}
                    className={`w-full mb-2 h-8 text-xs font-['Nunito'] border-[var(--liturgical-primary)]/30 ${newChallenge.invite_all ? 'bg-[var(--liturgical-primary)]/10 text-[var(--liturgical-primary)]' : 'text-[#6B665E]'}`}
                    data-testid="create-invite-all-toggle"
                  >
                    <Users className="w-3 h-3 mr-1.5" />
                    {newChallenge.invite_all ? `All ${contextLabel} members will be invited` : `Invite all ${contextLabel} members`}
                  </Button>
                )}
                <div className="flex gap-2">
                  <Input value={inviteSearch} onChange={(e) => setInviteSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleInviteSearch()} placeholder={`Search ${parishId || groupId ? contextLabel.toLowerCase() : ''} members...`} className="flex-1 border-[#E5E0D5] font-['Nunito'] text-sm h-8" data-testid="create-invite-search" />
                  <Button size="sm" variant="outline" onClick={handleInviteSearch} disabled={inviteSearching} className="border-[#E5E0D5] h-8 px-2">
                    {inviteSearching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                  </Button>
                </div>
                {inviteResults.length > 0 && (
                  <div className="mt-1.5 border border-[#E5E0D5] rounded-lg max-h-32 overflow-y-auto">
                    {inviteResults.map(u => {
                      const sel = selectedInvites.find(s => s.user_id === u.user_id);
                      return (
                        <button key={u.user_id} onClick={() => toggleInviteUser(u)} className="w-full flex items-center gap-2 px-2.5 py-1.5 hover:bg-[#F9F7F2] text-left text-sm font-['Nunito']" data-testid={`create-invite-user-${u.user_id}`}>
                          <div className="w-6 h-6 rounded-full bg-[#F0EBE0] flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {u.profile_photo ? <img src={u.profile_photo} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] font-bold text-[#6B665E]">{(u.name || "?")[0]}</span>}
                          </div>
                          <span className="truncate flex-1 text-[#2D2A26]">{u.name}</span>
                          {sel && <Badge className="text-[9px] h-4" style={{ backgroundColor: "var(--liturgical-primary)", color: "white" }}>Selected</Badge>}
                        </button>
                      );
                    })}
                  </div>
                )}
                {selectedInvites.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {selectedInvites.map(u => (
                      <Badge key={u.user_id} className="text-[10px] flex items-center gap-1 pr-1" style={{ backgroundColor: "var(--liturgical-primary)", color: "white" }}>
                        {u.name}
                        <button onClick={() => toggleInviteUser(u)} className="hover:opacity-70"><X className="w-2.5 h-2.5" /></button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="border-[#E5E0D5] font-['Nunito']">Cancel</Button>
            <Button onClick={handleCreate} disabled={!newChallenge.title.trim() || newChallenge.habits.length === 0} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid="submit-challenge-btn">
              Create Challenge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
